import {
  BRAND_LOGO_ENTITY_ID,
  EMPTY_CANVAS_CHROME,
  ImageEntityResolver,
  buildComplianceBootstrap,
  type BootstrapPayload,
  type CanvasBridgeAdapter,
  type CanvasChrome,
  type EntityDispatchResult,
  type EntitySnapshot,
} from '@continuum/canvas-runtime';
import type {CanvasEntityFieldsFragment} from './graphql/fragments';

export interface SharingCanvasBridgeOptions {
  sourceCode: string;
  entities: CanvasEntityFieldsFragment[];
  chrome?: CanvasChrome;
  downloadDocument: (
    canvasDocumentId: string,
    filename: string,
  ) => Promise<void>;
}

export class SharingCanvasBridge implements CanvasBridgeAdapter {
  private readonly sourceCode: string;
  private readonly snapshots: EntitySnapshot[];
  private readonly chrome: CanvasChrome;
  private readonly entitiesByPublicId: Map<string, CanvasEntityFieldsFragment>;
  private readonly downloadDocument: (
    id: string,
    filename: string,
  ) => Promise<void>;
  private readonly images: ImageEntityResolver;

  onRendered?: (occurredAt: string, source: string) => void;
  onRuntimeError?: (message: string, stack?: string) => void;

  constructor(opts: SharingCanvasBridgeOptions) {
    this.sourceCode = opts.sourceCode;
    this.chrome = opts.chrome ?? EMPTY_CANVAS_CHROME;
    this.images = new ImageEntityResolver();
    this.snapshots = opts.entities.map((e) => this.toSnapshot(e));
    this.entitiesByPublicId = new Map(
      opts.entities.map((e) => [e.publicId, e]),
    );
    this.downloadDocument = opts.downloadDocument;
  }

  private toSnapshot(entity: CanvasEntityFieldsFragment): EntitySnapshot {
    switch (entity.__typename) {
      case 'CanvasEntityDocumentShare':
        return {
          publicId: entity.publicId,
          type: 'DocumentShare',
          name: entity.name,
          canvasDocument: {
            name: entity.canvasDocument.name,
            byteSize: entity.canvasDocument.byteSize ?? null,
            contentType: entity.canvasDocument.contentType ?? null,
          },
        };
      case 'CanvasEntityImage':
        return this.images.create({
          publicId: entity.publicId,
          sourceId: entity.id,
          url: entity.imageUrl ?? '',
        });
    }
  }

  getBootstrap(): BootstrapPayload {
    const {brand, brandLogo, compliance, advisor} = this.chrome;
    const {
      entities,
      compliance: complianceConfig,
      advisor: advisorConfig,
    } = buildComplianceBootstrap({resolver: this.images, compliance, advisor});
    if (brandLogo) {
      entities.push(
        this.images.create({
          publicId: BRAND_LOGO_ENTITY_ID,
          ...brandLogo,
        }),
      );
    } else {
      this.images.forget(BRAND_LOGO_ENTITY_ID);
    }
    return {
      source: this.sourceCode,
      entities: [...this.snapshots, ...entities],
      brand,
      compliance: complianceConfig,
      advisor: advisorConfig,
    };
  }

  async dispatchEntityAction(
    id: string,
    action: string,
    payload: Record<string, unknown>,
  ): Promise<EntityDispatchResult> {
    if (this.images.has(id) && action === 'resolve') {
      return this.images.resolve(id, payload);
    }

    const entity = this.entitiesByPublicId.get(id);
    if (!entity) {
      return {ok: false, error: `unknown entity ${id}`};
    }

    if (
      entity.__typename === 'CanvasEntityDocumentShare' &&
      action === 'download'
    ) {
      await this.downloadDocument(
        entity.canvasDocument.id,
        entity.canvasDocument.name,
      );
      return {ok: true};
    }

    return {
      ok: false,
      error: `unsupported action ${action} for ${entity.__typename}`,
    };
  }
}
